"use client";

import { cn } from "@/lib/utils";
import {
  Clipboard,
  Mic,
  Image,
  Eraser,
  Settings2,
  Volume2,
  BrainCircuit,
  FileText,
  Copy,
  ArrowLeftRight,
  X,
  ChevronDown,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useDemoContent, mapProblemsToContextItems } from "@/utils/hooks/use-demo-content";
import type { DemoContentItem } from "@/lib/demo-content-types";
import { getLocaleConfig } from "@/i18n/locales";
import { getRtlDirection } from "@/utils/rtl";
import { convertLanguageCodeForHtml } from "@/utils/language-conversion";
import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";

// Helper to get language name from demo item
function getLanguageNameFromItem(item: DemoContentItem | null): string {
  if (!item) {
    return "German";
  }

  const localeId = convertLanguageCodeForHtml(item.outputLanguageId);
  const config = getLocaleConfig(localeId);
  if (config?.name) {
    return config.name;
  }

  if (typeof Intl !== "undefined" && typeof Intl.DisplayNames !== "undefined") {
    const displayNames = new Intl.DisplayNames(["en"], { type: "language" });
    const displayName = displayNames.of(localeId);
    if (displayName) {
      return displayName;
    }
  }

  return item.outputLanguageId;
}

// ============================================
// Reusable macOS Window Component
// ============================================

interface MacOSWindowProps {
  children: React.ReactNode;
  className?: string;
  title?: React.ReactNode;
  showTrafficLights?: boolean;
  rightContent?: React.ReactNode;
  /** Ref forwarded to the outer container */
  containerRef?: React.RefObject<HTMLDivElement | null>;
}

export function MacOSWindow({
  children,
  className,
  title = (
    <span className="text-lg font-bold bg-gradient-to-r from-[#2e86b3] to-[#164872] dark:from-[#aacfe1] dark:to-[#8a8dc5] bg-clip-text text-transparent">
      Starky
    </span>
  ),
  showTrafficLights = true,
  rightContent,
  containerRef,
}: MacOSWindowProps) {
  return (
    <div ref={containerRef} className={cn("w-full max-w-lg mx-auto", className)}>
      <div className="rounded-xl overflow-hidden shadow-2xl border border-border/50 dark:border-border/30 bg-background dark:bg-zinc-900">
        {/* Title Bar */}
        <div className="h-11 bg-muted/80 dark:bg-zinc-800/80 backdrop-blur-sm flex items-center px-4 border-b border-border/50 dark:border-zinc-700/50">
          {/* Traffic Light Buttons */}
          {showTrafficLights && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#FF5F57] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)]" />
              <div className="w-3 h-3 rounded-full bg-[#FEBC2E] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)]" />
              <div className="w-3 h-3 rounded-full bg-[#28C840] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)]" />
            </div>
          )}

          {/* Centered Title */}
          <div className="flex-1 flex justify-center">{title}</div>

          {/* Right side content */}
          {rightContent && (
            <div className="flex items-center gap-3">{rightContent}</div>
          )}
        </div>

        {/* Content */}
        <div className="bg-background dark:bg-zinc-900">{children}</div>
      </div>
    </div>
  );
}

// ============================================
// Translator App Mock
// ============================================

interface TranslatorMockProps {
  className?: string;
  inputText?: string;
  outputText?: string;
  sourceLang?: string;
  targetLang?: string;
  sourceLangId?: string;
  targetLangId?: string;
  userName?: string;
  showStarkySense?: boolean;
  /** Pass demo item from parent to ensure sync with other components */
  demoItem?: DemoContentItem | null;
  /** Ref to the Starky Sense button for arrow positioning */
  starkySenseButtonRef?: React.RefObject<HTMLDivElement | null>;
}

export function TranslatorMock({
  className,
  inputText,
  outputText,
  sourceLang,
  targetLang,
  sourceLangId,
  targetLangId,
  userName = "Bojan",
  showStarkySense = true,
  demoItem,
  starkySenseButtonRef,
}: TranslatorMockProps) {
  const tOutput = useTranslations("outputBox");
  const tStarkySense = useTranslations("starkySenseButton");
  // Only call hook if no demoItem provided (standalone usage)
  const hookData = useDemoContent();
  const item = demoItem ?? hookData.selectedItem;

  // Use provided values or fall back to demo content
  const effectiveInputText = inputText ?? hookData.sourceText ?? "Hello my friend! What did you do about the exam? Have you seen the professor lately?";
  const effectiveOutputText = outputText ?? item?.translatedText ?? "Hallo mein Freund! Was hast du mit der Prüfung gemacht? Hast du den Professor in letzter Zeit gesehen?";
  const effectiveSourceLang = sourceLang ?? hookData.sourceLanguageName ?? "English";
  const effectiveTargetLang = targetLang ?? hookData.targetLanguageName ?? getLanguageNameFromItem(item);
  const effectiveSourceLangId = sourceLangId ?? hookData.sourceLanguageId ?? "eng";
  const effectiveTargetLangId = targetLangId ?? item?.outputLanguageId ?? hookData.targetLanguageId ?? "deu";

  const inputDirection = getRtlDirection(effectiveSourceLangId);
  const outputDirection = getRtlDirection(effectiveTargetLangId);
  const inputTextAlignClass = inputDirection === "rtl" ? "text-right" : "text-left";
  const outputTextAlignClass = outputDirection === "rtl" ? "text-right" : "text-left";

  return (
    <MacOSWindow
      className={className}
      rightContent={
        <>
          <span className="text-sm">🇬🇧</span>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <span>{userName}</span>
            <span className="text-muted-foreground/50">≡</span>
          </div>
        </>
      }
    >
      {/* Language Selector Bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/30 dark:border-zinc-700/50">
        <div className="px-4 py-2 rounded-lg text-sm font-medium bg-primary/10 dark:bg-zinc-800 text-foreground">
          {effectiveSourceLang}
        </div>
        <button className="p-2 rounded-lg hover:bg-muted/50 dark:hover:bg-zinc-800 transition-colors">
          <ArrowLeftRight className="w-4 h-4 text-muted-foreground" />
        </button>
        <div className="px-4 py-2 rounded-lg text-sm font-medium bg-primary/10 dark:bg-zinc-800 text-foreground">
          {effectiveTargetLang}
        </div>
      </div>

      {/* Input Area */}
      <div
        dir={inputDirection}
        className={cn("min-h-[140px] p-4 border-b border-border/30 dark:border-zinc-700/50", inputTextAlignClass)}
      >
        <p className={cn("text-xl text-primary dark:text-blue-300 font-medium", inputTextAlignClass)}>
          {effectiveInputText}
        </p>
      </div>

      {/* Input Toolbar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border/30 dark:border-zinc-700/50">
        {[Clipboard, Mic, Image, Eraser].map((Icon, i) => (
          <div
            key={i}
            className="w-9 h-9 rounded-lg flex items-center justify-center bg-muted/50 dark:bg-zinc-800 hover:bg-muted dark:hover:bg-zinc-700 transition-colors"
          >
            <Icon className="w-4 h-4 text-muted-foreground" />
          </div>
        ))}
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-400 to-fuchsia-500">
          <BrainCircuit className="w-4 h-4 text-white" />
        </div>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-muted/50 dark:bg-zinc-800 hover:bg-muted dark:hover:bg-zinc-700 transition-colors">
          <Settings2 className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>

      {/* Output Area */}
      <div
        dir={outputDirection}
        className={cn("min-h-[140px] p-4 border-b border-border/30 dark:border-zinc-700/50 md:pr-10", outputTextAlignClass)}
      >
        <p className={cn("text-xl font-medium text-foreground", outputTextAlignClass)}>{effectiveOutputText}</p>
      </div>

      {/* Output Toolbar */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          {[Volume2, FileText, Copy].map((Icon, i) => (
            <div
              key={i}
              className="w-9 h-9 rounded-lg flex items-center justify-center bg-muted/50 dark:bg-zinc-800 hover:bg-muted dark:hover:bg-zinc-700 transition-colors"
            >
              <Icon className="w-4 h-4 text-muted-foreground" />
            </div>
          ))}
          {showStarkySense && (
            <div
              ref={starkySenseButtonRef}
              className="px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-400 to-fuchsia-500 text-white text-xs font-medium flex items-center gap-1.5"
            >
              <BrainCircuit className="w-3 h-3" />
              {tStarkySense("contextRequired")}
            </div>
          )}
        </div>
      </div>

      {/* Copy to Clipboard Footer */}
      <div className="flex justify-center py-3 border-t border-border/30 dark:border-zinc-700/50">
        <div className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
          <Copy className="w-4 h-4" />
          {tOutput("copyButton")}
        </div>
      </div>
    </MacOSWindow>
  );
}

// ============================================
// Starky Sense Dialog Mock
// ============================================

interface StarkySenseContextItem {
  label: string;
  explanation: string;
  selectedOption: string;
}

interface StarkySenseDialogMockProps {
  className?: string;
  targetLanguage?: string;
  contextItems?: StarkySenseContextItem[];
  /** Pass demo item from parent to ensure sync with other components */
  demoItem?: DemoContentItem | null;
  /** Ref to the dialog container for arrow positioning */
  dialogRef?: React.RefObject<HTMLDivElement | null>;
}

export function StarkySenseDialogMock({
  className,
  targetLanguage,
  contextItems,
  demoItem,
  dialogRef,
}: StarkySenseDialogMockProps) {
  const t = useTranslations("starkySenseDialog");
  // Only call hook if no demoItem provided (standalone usage)
  const hookData = useDemoContent();
  const item = demoItem ?? hookData.selectedItem;

  // Use provided values or fall back to demo content
  const effectiveTargetLanguage = targetLanguage ?? hookData.targetLanguageName ?? getLanguageNameFromItem(item);
  const effectiveContextItems = contextItems ?? mapProblemsToContextItems(item);

  return (
    <MacOSWindow
      className={className}
      containerRef={dialogRef}
      showTrafficLights={false}
      title={
        <span className="text-lg font-bold bg-gradient-to-r from-[#bf4fce] to-[#335cb4] dark:from-[#b3d4ff] dark:to-[#db8fee] bg-clip-text text-transparent">
          Starky Sense!
        </span>
      }
      rightContent={
        <button className="p-1 rounded hover:bg-muted/50 dark:hover:bg-zinc-700 transition-colors">
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      }
    >
      {/* Dialog Content */}
      <div className="p-5">
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-foreground mb-1">
            {t("title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("descriptionSm", { targetLanguage: effectiveTargetLanguage })}
          </p>
        </div>

        {/* Context Items */}
        <div className="space-y-4">
          {effectiveContextItems.length > 0 ? (
            effectiveContextItems.map((item, index) => (
              <div
                key={index}
                className="space-y-2"
              >
                {/* Label + Explanation */}
                <p className="text-sm text-muted-foreground">
                  <span className="px-1.5 py-0.5 rounded bg-muted dark:bg-zinc-800 text-xs font-medium text-foreground mr-2">
                    {item.label}
                  </span>
                  {item.explanation}
                </p>

                {/* Dropdown */}
                <div className="relative overflow-hidden rounded-lg">
                  <div
                    className="w-full px-3 py-2.5 rounded-lg border border-border/50 dark:border-zinc-700 bg-background dark:bg-zinc-800/50 text-sm text-foreground flex items-center justify-between cursor-pointer hover:border-border dark:hover:border-zinc-600 transition-colors"
                  >
                    <span>{item.selectedOption}</span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </div>
                  {/* Border shimmer */}
                  <div
                    className="absolute inset-0 rounded-lg pointer-events-none border border-transparent animate-shimmer -translate-x-full"
                    style={{
                      animationDelay: `${index * 0.5}s`,
                      background: 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(255,255,255,0.3) 50%, transparent 60%, transparent 100%)',
                      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      WebkitMaskComposite: 'xor',
                      maskComposite: 'exclude',
                      padding: '1px',
                    }}
                  />
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">{t("noProblems")}</p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <button className="px-4 py-2 rounded-lg border border-border/50 dark:border-zinc-700 bg-background dark:bg-zinc-800 text-sm font-medium text-foreground hover:bg-muted dark:hover:bg-zinc-700 transition-colors">
            {t("ignore")}
          </button>
          <button className="px-4 py-2 rounded-lg bg-foreground dark:bg-white text-background dark:text-zinc-900 text-sm font-medium hover:opacity-90 transition-opacity">
            {t("revise")}
          </button>
        </div>
      </div>
    </MacOSWindow>
  );
}

// Legacy export for backwards compatibility
export const AppWindowMock = TranslatorMock;

// ============================================
// Animated Connection Arrow (Dynamic)
// ============================================

interface ConnectionPoint {
  x: number;
  y: number;
}

interface AnimatedConnectionArrowProps {
  start: ConnectionPoint;
  end: ConnectionPoint;
  className?: string;
}

/**
 * Dynamically renders an animated curved arrow between two points.
 * Creates a single downward arc with an arrowhead at the end.
 */
function AnimatedConnectionArrow({ start, end, className }: AnimatedConnectionArrowProps) {
  // Stable ID for gradient (computed once per component instance)
  const gradientId = useMemo(() => `arrow-gradient-${Math.random().toString(36).slice(2, 9)}`, []);

  // Calculate curve parameters
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const curveIntensity = Math.min(distance * 0.4, 120);

  // Both control points pull downward to create a single arc
  const cp1 = { x: start.x + dx * 0.33, y: start.y + curveIntensity };
  const cp2 = { x: end.x - dx * 0.1, y: end.y + curveIntensity * 0.5 };

  const curvePath = `M ${start.x} ${start.y} C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${end.x} ${end.y}`;

  // Arrowhead: direction from cp2 to end gives the tangent at curve's end
  const arrowSize = 16;
  const angle = Math.atan2(end.y - cp2.y, end.x - cp2.x);
  const wingSpread = Math.PI / 6; // 30° spread each side

  const arrowPath = `M ${end.x - Math.cos(angle - wingSpread) * arrowSize} ${end.y - Math.sin(angle - wingSpread) * arrowSize} L ${end.x} ${end.y} L ${end.x - Math.cos(angle + wingSpread) * arrowSize} ${end.y - Math.sin(angle + wingSpread) * arrowSize}`;

  return (
    <svg
      className={cn("pointer-events-none absolute inset-0 overflow-visible", className)}
      style={{ width: '100%', height: '100%' }}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="100%" stopColor="#c084fc" />
        </linearGradient>
      </defs>

      {/* Main curved path - static dotted background */}
      <path
        d={curvePath}
        stroke={`url(#${gradientId})`}
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray="1 8"
        strokeOpacity="0.3"
        fill="none"
      />

      {/* Animated flowing dots */}
      <path
        d={curvePath}
        stroke={`url(#${gradientId})`}
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="4 14"
        fill="none"
        className="animate-flow-dots"
      />

      {/* Dynamic arrowhead */}
      <path
        d={arrowPath}
        stroke={`url(#${gradientId})`}
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

// ============================================
// Demo Section - Wrapper for synced demo content
// ============================================

/**
 * Wrapper component that ensures TranslatorMock and StarkySenseDialogMock
 * show the same demo content. Calls useDemoContent once and passes to both.
 * Creates a connected visual with rotation and animated arrow.
 * The arrow dynamically connects the Starky Sense button to the dialog.
 */
export function DemoSection({ className }: { className?: string }) {
  const { sourceLanguageId, sourceLanguageName, sourceText, selectedItem, targetLanguageId, targetLanguageName } = useDemoContent();
  const contextItems = useMemo(() => mapProblemsToContextItems(selectedItem), [selectedItem]);

  // Refs for dynamic arrow positioning
  const containerRef = useRef<HTMLDivElement>(null);
  const starkySenseButtonRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // State for arrow endpoints (relative to container)
  const [arrowPoints, setArrowPoints] = useState<{ start: ConnectionPoint; end: ConnectionPoint } | null>(null);

  // Calculate arrow positions based on element positions
  const updateArrowPositions = useCallback(() => {
    if (!containerRef.current || !starkySenseButtonRef.current || !dialogRef.current) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const buttonRect = starkySenseButtonRef.current.getBoundingClientRect();
    const dialogRect = dialogRef.current.getBoundingClientRect();

    // Start point: slightly inside the Starky Sense button (center-right minus a few px)
    const start: ConnectionPoint = {
      x: buttonRect.right - 5 - containerRect.left,
      y: buttonRect.top + buttonRect.height / 2 - containerRect.top,
    };

    // End point: bottom-left corner of the dialog, inside the window
    const end: ConnectionPoint = {
      x: dialogRect.left + 20 - containerRect.left,
      y: dialogRect.bottom - 50 - containerRect.top,
    };

    setArrowPoints({ start, end });
  }, []);

  // Update on mount and resize - use requestAnimationFrame for smooth updates
  useEffect(() => {
    updateArrowPositions();

    // Use rAF for smooth resize updates without debouncing
    let rafId: number;
    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateArrowPositions);
    };

    window.addEventListener('resize', handleResize);

    // Observe layout changes on container and dialog (dialog resizes when content changes)
    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    if (dialogRef.current) {
      resizeObserver.observe(dialogRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [updateArrowPositions]);

  return (
    <div ref={containerRef} className={cn("relative max-w-5xl mx-auto", className)}>
      {/* Dynamic Connection Arrow - positioned absolutely over the container, z-40 to be above both windows */}
      {arrowPoints && (
        <div className="hidden md:block absolute inset-0 z-40 pointer-events-none">
          <AnimatedConnectionArrow start={arrowPoints.start} end={arrowPoints.end} />
        </div>
      )}

      {/* Container for positioned windows */}
      <div className="relative flex flex-col md:flex-row items-center md:items-start justify-center gap-8 md:gap-0 min-h-[500px] md:min-h-[650px] px-4">
        {/* Main Translator Window - rotated left */}
        <div className="relative z-10 md:-rotate-6 origin-center md:mt-20">
          <TranslatorMock
            inputText={sourceText ?? undefined}
            sourceLang={sourceLanguageName}
            sourceLangId={sourceLanguageId ?? undefined}
            targetLang={targetLanguageName}
            targetLangId={targetLanguageId ?? undefined}
            demoItem={selectedItem}
            starkySenseButtonRef={starkySenseButtonRef}
            className="w-[320px] sm:w-[380px] md:w-[400px] shadow-2xl rounded-xl"
          />
        </div>

        {/* Starky Sense Dialog - overlapping, rotated right */}
        <div className="relative z-30 md:rotate-2 origin-center md:mt-0 md:-ml-6">
          <StarkySenseDialogMock
            targetLanguage={targetLanguageName}
            contextItems={contextItems}
            demoItem={selectedItem}
            dialogRef={dialogRef}
            className="w-[320px] sm:w-[380px] md:w-[420px] shadow-2xl rounded-xl"
          />
        </div>
      </div>
    </div>
  );
}
